import { useEffect, useState, Component } from "react";
import axios from "axios";
import "@/App.css";
import Phase2Panel from "@/Phase2";
import RagPanel from "@/Rag";
import {
  Database, Languages, GitBranch, ShieldCheck, Link2, Boxes,
  FolderTree, Table2, Layers, KeyRound, Fingerprint, ChevronRight, Loader2,
  FileText, Activity, ShieldAlert, ClipboardCheck, MessageSquare, Bug, FlaskConical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidUpdate(prev) { if (prev.tab !== this.props.tab && this.state.error) this.setState({ error: null }); }
  render() {
    if (this.state.error)
      return <div className="ev-card ev-muted" data-testid="tab-error">This view failed to render: {String(this.state.error.message || this.state.error)}</div>;
    return this.props.children;
  }
}

const TABS = [
  { id: "overview", label: "Overview", icon: Layers, phase: 1 },
  { id: "collections", label: "Collections", icon: Database, phase: 1 },
  { id: "indexes", label: "Indexes", icon: KeyRound, phase: 1 },
  { id: "examples", label: "Example Docs", icon: Table2, phase: 1 },
  { id: "linking", label: "Multilingual & Entities", icon: GitBranch, phase: 1 },
  { id: "structure", label: "Folder Structure", icon: FolderTree, phase: 1 },
  { id: "scraped", label: "Scraped Articles", icon: FileText, phase: 2 },
  { id: "runs", label: "Scrape Runs", icon: Activity, phase: 2 },
  { id: "review", label: "Review Queue", icon: ShieldAlert, phase: 2 },
  { id: "validation", label: "Validation", icon: ClipboardCheck, phase: 2 },
  { id: "chat", label: "Chat", icon: MessageSquare, phase: 3 },
  { id: "ragdebug", label: "RAG Debug", icon: Bug, phase: 3 },
  { id: "eval", label: "Evaluation", icon: FlaskConical, phase: 3 },
];

const Json = ({ data }) => (
  <pre className="ev-json" data-testid="json-block">
    {JSON.stringify(data, null, 2)}
  </pre>
);

function Overview({ arch }) {
  return (
    <div className="ev-grid" data-testid="overview-panel">
      <div className="ev-card ev-span2">
        <div className="ev-card-h"><Layers size={16} /> Scope</div>
        <p className="ev-muted">{arch.scope}</p>
        <div className="ev-badges">
          <span className="ev-badge ev-badge-gold">{arch.phase}</span>
          <span className="ev-badge">retrieval: {arch.preferredRetrievalLanguage}</span>
          <span className="ev-badge">{arch.collections.length} collections</span>
          <span className="ev-badge">{arch.languages.length} languages</span>
        </div>
      </div>

      <div className="ev-card">
        <div className="ev-card-h"><Languages size={16} /> Languages</div>
        <ul className="ev-lang-list">
          {arch.languages.map((l) => (
            <li key={l.code} data-testid={`lang-${l.code}`}>
              <span className="ev-mono">{l.code}</span>
              <span className="ev-native">{l.nativeName}</span>
              <span className="ev-badge">{l.role}</span>
              {l.canonical && <span className="ev-badge ev-badge-gold">preferred</span>}
              {l.direction === "rtl" && <span className="ev-badge">rtl</span>}
            </li>
          ))}
        </ul>
      </div>

      <div className="ev-card">
        <div className="ev-card-h"><Boxes size={16} /> Embeddings (reserved)</div>
        <p className="ev-muted">{arch.embeddings.status}</p>
        <p className="ev-muted ev-small">{arch.embeddings.field}</p>
        <div className="ev-badges">
          <span className="ev-badge">dim: {String(arch.embeddings.intendedDefaultDim)}</span>
          <span className="ev-badge">{arch.embeddings.vectorSearch.similarity}</span>
          <span className="ev-badge">{arch.embeddings.vectorSearch.engine}</span>
        </div>
      </div>

      <div className="ev-card ev-span2">
        <div className="ev-card-h"><ShieldCheck size={16} /> Provenance & trust priority</div>
        <p className="ev-muted">{arch.provenanceRules.rule}</p>
        <p className="ev-muted ev-small">{arch.provenanceRules.citations}</p>
        <div className="ev-badges">
          {Object.entries(arch.provenanceRules.trustPriority || {})
            .sort((a, b) => b[1] - a[1])
            .map(([method, score]) => (
              <span key={method} className="ev-badge ev-mono">{method}: {score}</span>
            ))}
        </div>
      </div>
    </div>
  );
}

function Collections({ arch, schemas }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="ev-list" data-testid="collections-panel">
      {arch.collections.map((c) => {
        const isOpen = open === c.name;
        return (
          <div key={c.name} className="ev-acc">
            <button
              className="ev-acc-h"
              data-testid={`collection-toggle-${c.name}`}
              onClick={() => setOpen(isOpen ? null : c.name)}
            >
              <ChevronRight size={16} className={`ev-chev ${isOpen ? "open" : ""}`} />
              <Database size={15} />
              <span className="ev-mono ev-strong">{c.name}</span>
              <span className="ev-muted ev-flex1">{c.purpose}</span>
              <span className="ev-badge">{c.indexCount} idx</span>
            </button>
            {isOpen && schemas[c.name] && (
              <div className="ev-acc-b">
                <Json data={schemas[c.name].jsonSchema} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Indexes({ indexes }) {
  return (
    <div className="ev-list" data-testid="indexes-panel">
      {Object.entries(indexes).map(([coll, defs]) => (
        <div key={coll} className="ev-card">
          <div className="ev-card-h"><KeyRound size={16} /> <span className="ev-mono">{coll}</span></div>
          <table className="ev-table">
            <thead>
              <tr><th>Name</th><th>Keys</th><th>Options</th><th>Purpose</th></tr>
            </thead>
            <tbody>
              {defs.map((d) => {
                const opts = d.options || {};
                const isVector = opts.atlasSearch;
                return (
                  <tr key={opts.name} className={isVector ? "ev-row-reserved" : ""}>
                    <td className="ev-mono">{opts.name}{isVector && <span className="ev-badge ev-badge-gold">reserved</span>}</td>
                    <td className="ev-mono ev-small">
                      {d.keys.map((k) => `${k[0]}:${k[1]}`).join(", ")}
                    </td>
                    <td className="ev-mono ev-small">
                      {opts.unique ? "unique " : ""}
                      {isVector ? `atlasVector(${opts.numDimensions}d,${opts.similarity})` : ""}
                    </td>
                    <td className="ev-muted ev-small">{d.description}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function Examples({ examples }) {
  const names = Object.keys(examples);
  const [active, setActive] = useState(names[0]);
  return (
    <div data-testid="examples-panel">
      <div className="ev-pills">
        {names.map((n) => (
          <button
            key={n}
            className={`ev-pill ${active === n ? "active" : ""}`}
            data-testid={`example-pill-${n}`}
            onClick={() => setActive(n)}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="ev-card">
        <Json data={examples[active]} />
      </div>
    </div>
  );
}

function Linking({ arch, examples }) {
  const art = examples.articles;
  return (
    <div className="ev-list" data-testid="linking-panel">
      <div className="ev-card">
        <div className="ev-card-h"><Link2 size={16} /> One canonical article → 8 language versions</div>
        <p className="ev-muted">{arch.multilingualStrategy.linking}</p>
        <div className="ev-tree">
          <div className="ev-tree-root">
            <span className="ev-mono ev-strong">articles._id = {art._id}</span>
            <span className="ev-muted"> slug: {art.slug}</span>
          </div>
          {art.availableLanguages.map((lang) => (
            <div key={lang} className="ev-tree-node" data-testid={`link-lang-${lang}`}>
              <span className="ev-branch">└─</span>
              <span className="ev-mono">article_translations</span>
              <span className="ev-badge">{lang}</span>
              <span className="ev-muted ev-small">{art.localizedSourceUrls[lang]}</span>
              {lang === "en" && <span className="ev-badge ev-badge-gold">canonical</span>}
            </div>
          ))}
        </div>
        <p className="ev-muted ev-small">{arch.multilingualStrategy.factsVsTranslations}</p>
      </div>

      <div className="ev-card">
        <div className="ev-card-h"><Fingerprint size={16} /> Entity normalization — translated names → one canonical entity</div>
        <p className="ev-muted">{arch.entityNormalization.how}</p>
        <div className="ev-tree">
          <div className="ev-tree-root">
            <span className="ev-mono ev-strong">generals._id = {examples.generals._id}</span>
            <span className="ev-muted"> {examples.generals.name} (norm: {examples.generals.nameNormalized})</span>
          </div>
          {examples.generals.localizedNames.map((ln) => (
            <div key={ln.language} className="ev-tree-node" data-testid={`entity-name-${ln.language}`}>
              <span className="ev-branch">└─</span>
              <span className="ev-badge">{ln.language}</span>
              <span className="ev-mono">{ln.value}</span>
              <span className={`ev-badge ${ln.sourceType === "wiki" ? "ev-badge-green" : "ev-badge-amber"}`}>
                {ln.sourceType}
              </span>
            </div>
          ))}
        </div>
        <p className="ev-muted ev-small"><code>{arch.entityNormalization.normalization}</code></p>
      </div>

      <div className="ev-card">
        <div className="ev-card-h"><GitBranch size={16} /> Canonical URL matching (translated URLs → same article)</div>
        <p className="ev-muted">{arch.canonicalUrlMatching.problem}</p>
        <ol className="ev-ol">
          {arch.canonicalUrlMatching.signalsInPriorityOrder.map((s, i) => (
            <li key={i} className="ev-muted ev-small">{s}</li>
          ))}
        </ol>
        <p className="ev-muted ev-small">{arch.canonicalUrlMatching.storage}</p>
      </div>
    </div>
  );
}

function Structure({ folder }) {
  const render = (obj, depth = 0) =>
    Object.entries(obj).map(([k, v]) => (
      <div key={k} style={{ paddingLeft: depth * 22 }} className="ev-fs-row">
        <FolderTree size={13} className="ev-fs-icon" />
        <span className="ev-mono ev-strong">{k}</span>
        {typeof v === "string" ? (
          <span className="ev-muted ev-small"> — {v}</span>
        ) : (
          <div className="ev-fs-children">{render(v, depth + 1)}</div>
        )}
      </div>
    ));
  return <div className="ev-card" data-testid="structure-panel">{render(folder)}</div>;
}

export default function DebugConsole() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/architecture`),
      axios.get(`${API}/schema`),
      axios.get(`${API}/indexes`),
      axios.get(`${API}/examples`),
    ])
      .then(([a, s, i, e]) =>
        setData({ arch: a.data, schemas: s.data, indexes: i.data, examples: e.data })
      )
      .catch((err) => setError(err.message));
  }, []);

  if (error)
    return <div className="ev-loading" data-testid="error-state">Failed to load: {error}</div>;
  if (!data)
    return (
      <div className="ev-loading" data-testid="loading-state">
        <Loader2 className="ev-spin" /> Loading architecture…
      </div>
    );

  return (
    <div className="ev-app" data-testid="app-root">
      <header className="ev-header">
        <div className="ev-title">
          <div className="ev-logo">ES</div>
          <div>
            <h1>Evony Strategist <span className="ev-v2">V2</span></h1>
            <p className="ev-sub">Phase 1–3 · RAG debug console</p>
          </div>
        </div>
        <span className="ev-badge ev-badge-gold" data-testid="phase-badge">Phase 1–3</span>
      </header>

      <nav className="ev-nav">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              className={`ev-nav-btn ${tab === t.id ? "active" : ""} ${t.phase === 2 ? "p2" : t.phase === 3 ? "p3" : ""}`}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </nav>

      <main className="ev-main">
        <ErrorBoundary tab={tab}>
          {tab === "overview" && <Overview arch={data.arch} />}
          {tab === "collections" && <Collections arch={data.arch} schemas={data.schemas} />}
          {tab === "indexes" && <Indexes indexes={data.indexes} />}
          {tab === "examples" && <Examples examples={data.examples} />}
          {tab === "linking" && <Linking arch={data.arch} examples={data.examples} />}
          {tab === "structure" && <Structure folder={data.arch.folderStructure} />}
          {["scraped", "runs", "review", "validation"].includes(tab) && <Phase2Panel view={tab} />}
          {["chat", "ragdebug", "eval"].includes(tab) && <RagPanel view={tab} />}
        </ErrorBoundary>
      </main>

      <footer className="ev-footer">
        Phase 1 complete — architecture only. Scraping, embeddings, RAG & chat are deferred.
      </footer>
    </div>
  );
}
